import type { ChangeEvent } from 'react';

import { type MetadataInput } from '../../stores/useAppStore';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';

export interface MetadataFormProps {
  readonly value: MetadataInput;
  readonly onChange: (patch: Partial<MetadataInput>) => void;
  readonly errors: Readonly<Record<string, string | undefined>>;
  readonly disabled?: boolean;
}

export function MetadataForm(props: MetadataFormProps): JSX.Element {
  const { value, onChange, errors, disabled = false } = props;
  const handle = makeChangeHandler(onChange);

  return (
    <form className="space-y-5" aria-label="車両メタデータフォーム">
      <BasicFields value={value} errors={errors} disabled={disabled} handle={handle} />
      <EquipmentField value={value} errors={errors} disabled={disabled} handle={handle} />
      <AppealFields value={value} errors={errors} disabled={disabled} handle={handle} />
    </form>
  );
}

type ChangeHandler = (
  key: keyof MetadataInput,
) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;

function makeChangeHandler(onChange: MetadataFormProps['onChange']): ChangeHandler {
  return (key) => (e) => onChange({ [key]: e.target.value } as Partial<MetadataInput>);
}

interface SectionProps {
  readonly value: MetadataInput;
  readonly errors: MetadataFormProps['errors'];
  readonly disabled: boolean;
  readonly handle: ChangeHandler;
}

function BasicFields({ value, errors, disabled, handle }: SectionProps): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <Field label="車種" error={errors['model']}>
        <Input
          value={value.modelInput}
          placeholder="例: BMW X5"
          onChange={handle('modelInput')}
          disabled={disabled}
          autoComplete="off"
        />
      </Field>
      <Field label="年式 (西暦)" error={errors['year']}>
        <Input
          value={value.yearInput}
          placeholder="例: 2020"
          inputMode="numeric"
          onChange={handle('yearInput')}
          disabled={disabled}
        />
      </Field>
      <Field label="グレード" error={errors['grade']}>
        <Input
          value={value.gradeInput}
          placeholder="例: xDrive35d Mスポーツ"
          onChange={handle('gradeInput')}
          disabled={disabled}
        />
      </Field>
      <Field label="走行距離 (km)" error={errors['mileage']}>
        <Input
          value={value.mileageInput}
          placeholder="例: 42,500"
          inputMode="numeric"
          onChange={handle('mileageInput')}
          disabled={disabled}
        />
      </Field>
    </div>
  );
}

function EquipmentField({ value, errors, disabled, handle }: SectionProps): JSX.Element {
  return (
    <Field
      label="装備"
      error={errors['equipment']}
      hint="カンマ or 改行区切り。先頭が [装備1] に使われる"
    >
      <Textarea
        value={value.equipmentText}
        placeholder={`例:\n20インチホイール\nサンルーフ\n本革シート`}
        onChange={handle('equipmentText')}
        disabled={disabled}
        rows={3}
      />
    </Field>
  );
}

function AppealFields({ value, errors, disabled, handle }: SectionProps): JSX.Element {
  return (
    <div className="space-y-3">
      <div className="text-text-secondary text-xs font-medium">アピールポイント (3 つ)</div>
      <Field label="アピール1" error={errors['appeal1']}>
        <Input
          value={value.appeal1}
          placeholder="例: ワンオーナー車"
          onChange={handle('appeal1')}
          disabled={disabled}
        />
      </Field>
      <Field label="アピール2" error={errors['appeal2']}>
        <Input
          value={value.appeal2}
          placeholder="例: 記録簿完備"
          onChange={handle('appeal2')}
          disabled={disabled}
        />
      </Field>
      <Field label="アピール3" error={errors['appeal3']}>
        <Input
          value={value.appeal3}
          placeholder="例: 禁煙車"
          onChange={handle('appeal3')}
          disabled={disabled}
        />
      </Field>
    </div>
  );
}

interface FieldProps {
  readonly label: string;
  readonly error?: string | undefined;
  readonly hint?: string;
  readonly children: React.ReactNode;
}

function Field(props: FieldProps): JSX.Element {
  return (
    <div className="space-y-1.5">
      <Label>{props.label}</Label>
      {props.children}
      {props.hint && !props.error ? (
        <p className="text-text-tertiary text-[11px]">{props.hint}</p>
      ) : null}
      {props.error ? <p className="text-status-error text-[11px]">{props.error}</p> : null}
    </div>
  );
}
